import {
  SearchAccordion as ComponentSearchAccordion,
  useTableRowSelection,
} from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import {
  useCommentSearchAccordionOpenSetting,
  useLastCommentSearchHistory,
} from '@/features/comment/hooks/local-storage';
import { useCommentListSearchQueryStates } from '@/features/comment/hooks/search-query-states';
import { CommentSearchFormSchema } from '@/features/comment/models/search';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';
import { CommentListSearchFilterTags } from './CommentListSearchFilterTags';
import { CommentListSearchForm } from './CommentListSearchForm';

type FormValues = z.infer<typeof CommentSearchFormSchema>;

export const CommentListSearchAccordion: FC = () => {
  const { data: me } = useMe();
  const {
    commentSearchAccordionOpenSetting,
    setCommentSearchAccordionOpenSetting,
  } = useCommentSearchAccordionOpenSetting(me?.id ?? '');
  const [searchQueryStates, setSearchQueryStates] =
    useCommentListSearchQueryStates();

  const { clearAllSelections } = useTableRowSelection();
  const { setSearchHistoryStorage } = useLastCommentSearchHistory();

  const { mutateAsync } = useCreateQueryStore();
  const treeParamsData = useQueryStore(searchQueryStates.cascader ?? undefined);

  const defaultValues = useMemo(() => {
    return {
      createdMonthRange: [
        searchQueryStates.createdAtFrom
          ? dayjs(searchQueryStates.createdAtFrom)
          : undefined,
        searchQueryStates.createdAtTo
          ? dayjs(searchQueryStates.createdAtTo)
          : undefined,
      ],
      keyword: searchQueryStates.keyword,
      resolvedStatus: searchQueryStates.resolvedStatus,
      readStatus: searchQueryStates.readStatus,
      publishType: searchQueryStates.publishType,
      labelId: searchQueryStates.labelId,
      tagIds:
        searchQueryStates.tagIds?.at(0) === 'null'
          ? []
          : searchQueryStates.tagIds,
      authorId: searchQueryStates.authorId,
      assigneeId: searchQueryStates.assigneeId,
      organizationIds: treeParamsData.data?.query?.organizationIds ?? [],
      pinpointKeys: treeParamsData.data?.query?.pinpointKeys?.flat() ?? [],
      fiscalYear: searchQueryStates.fiscalYear
        ? dayjs().year(searchQueryStates.fiscalYear)
        : null,
    } as const satisfies FormValues;
  }, [
    searchQueryStates,
    treeParamsData.data?.query?.organizationIds,
    treeParamsData.data?.query?.pinpointKeys,
  ]);

  const formMethods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(CommentSearchFormSchema),
  });

  const { reset, setValue } = formMethods;

  useEffect(() => {
    // NOTE: カスケーダーの選択肢がある場合は、その選択肢を設定する
    if (defaultValues.organizationIds?.length) {
      setValue('organizationIds', defaultValues.organizationIds);
    }
    // 現状はセレクトですがBEの実装に合わせて
    if (defaultValues.pinpointKeys?.length) {
      setValue('pinpointKeys', defaultValues.pinpointKeys);
    }
  }, [setValue, defaultValues.organizationIds, defaultValues.pinpointKeys]);

  // localStorageの値を反映させるため
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSearch = useCallback(
    async ({ createdMonthRange, ...values }: FormValues) => {
      const createdAtFrom = createdMonthRange?.[0]?.format('YYYY-MM-DD');
      const createdAtTo = createdMonthRange?.[1]?.format('YYYY-MM-DD');

      const queryAndLeafs = generateQueryAndLeafs({
        organizationIds: values.organizationIds,
        pinpointKeys: values.pinpointKeys.map((item) => [item]),
      });

      const currLeafs = queryAndLeafs?.queryLeafs;
      const prevLeafs = treeParamsData.data?.queryLeafs;

      const isDifferent = currLeafs && !isEqual(currLeafs, prevLeafs);
      const requestCascaderId =
        values.organizationIds.length || values.pinpointKeys.length
          ? isDifferent
            ? (await mutateAsync(queryAndLeafs)).id
            : searchQueryStates.cascader
          : null;

      const nextSearchQueryStates = {
        ...values,
        cascader: requestCascaderId,
        createdAtFrom: createdAtFrom ?? null,
        createdAtTo: createdAtTo ?? null,
        fiscalYear: values.fiscalYear?.year() ?? null,
        page: 1,
      };

      // localStorageに保存するため
      const parsedRecord = JSON.parse(JSON.stringify(nextSearchQueryStates));
      setSearchHistoryStorage(parsedRecord);
      setSearchQueryStates(nextSearchQueryStates);
      clearAllSelections();
    },
    [
      clearAllSelections,
      mutateAsync,
      setSearchHistoryStorage,
      setSearchQueryStates,
      searchQueryStates.cascader,
      treeParamsData.data?.queryLeafs,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <ComponentSearchAccordion
        defaultOpen={commentSearchAccordionOpenSetting?.comment}
        onChange={(isOpen) => {
          setCommentSearchAccordionOpenSetting({
            ...commentSearchAccordionOpenSetting,
            comment: isOpen,
          });
        }}
        filterTags={
          <div>
            <CommentListSearchFilterTags />
          </div>
        }
        searchForm={
          <CommentListSearchForm
            onSearch={onSearch}
            resetFormValues={{
              createdMonthRange: null,
              resolvedStatus: 'all',
              keyword: null,
              readStatus: 'all',
              publishType: 'open',
              labelId: null,
              authorId: null,
              assigneeId: null,
              tagIds: [],
              organizationIds: [],
              pinpointKeys: [],
              fiscalYear: null,
            }}
          />
        }
      />
    </FormProvider>
  );
};
